import React from 'react'
import {
  Button,
  StepsPrevTrigger,
} from '@lightspeed/unified-components-helios-theme/react'
import type { ApiCustomer } from '@vend/types'
import { StepBreadcrumbs } from '../StepBreadcrumbs/StepBreadcrumbs'
import type { CustomerInfo, PartialCustomerGroup } from '../../../common/types'
import { STEPS } from '../types'
import { FulfilmentType } from '../../../../../../models/fulfilment'
import { LineItemFulfilmentNote } from '../../../../../sale/sale-line-item/fulfilment/LineItemFulfilmentNote'
import { LineItemFulfilmentAddress } from '../../../../../sale/sale-line-item/fulfilment/LineItemFulfilmentAddress'
import { FormButtonOnEmptyShippingAddress } from '../../../../../sale/sale-line-item/fulfilment/address/FormButtonOnEmptyShippingAddress'
import { useStep3Model } from './useStep3Model'

export interface Step3Props {
  onStepChange: any
  onClose: (hasMarked: boolean) => void
  handleSaleNoteChange: (note: string) => void
  type: FulfilmentType
  newCustomer: CustomerInfo | null
  selectedCustomer: ApiCustomer | null
  group?: PartialCustomerGroup
  fulfillmentOutletID: string | null
  perLineFulfilmentOutletId?: string
  contactChanged: boolean
  note: string
  totalQuantity: number
}

export const Step3: React.FC<Step3Props> = props => {
  const models = useStep3Model(props)

  if (models.addressModalPending) {
    return null
  }

  return (
    <div className="uc-base w-[640px] max-w-[calc(100vw-2rem)] rounded-lg bg-bg-neutral-top shadow-elevated">
      <StepBreadcrumbs
        step={STEPS.DETAILS}
        isAddingCustomer={Boolean(props.newCustomer)}
        onStepChange={props.onStepChange}
      >
        <div className="px-8 pt-6">
          <h2 className="typography-heading-md text-fg-neutral-default">
            {models.detailTitle}
          </h2>
          <p className="mt-2 typography-body-sm text-fg-neutral-soft">
            {models.detailDescription}
          </p>
        </div>

        <div className="px-8 py-5">
          {models.shouldShowFulfilmentAddress && (
            <div className="mb-8">
              <LineItemFulfilmentAddress
                fulfillmentDetail={models.localFulfilmentDetail}
                isModalOpeningOnError={!models.addressModalOpenedRef.current}
                onSave={models.handleSaveAddress}
              />
            </div>
          )}

          <LineItemFulfilmentNote
            note={props.note}
            onChange={props.handleSaleNoteChange}
            isDisabled={models.loading}
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-border-neutral-soft px-8">
          <StepsPrevTrigger>
            <Button
              appearance="secondary"
              disabled={models.loading}
              customDataAttributes={{
                container: {
                  'data-track': props.newCustomer
                    ? 'back-add-step'
                    : 'back-customer-step',
                },
              }}
            >
              {models.backLabel}
            </Button>
          </StepsPrevTrigger>
          {models.isShippingAddressFixRequired ? (
            <FormButtonOnEmptyShippingAddress
              loading={models.loading}
              onClose={models.handleClose}
              fulfilmentDetail={models.localFulfilmentDetail}
              handleEmptyShippingCorrectBillingAddress={
                models.handleBillingAddressForShippingAddress
              }
              onSave={models.handleSaveAddressAndMark}
            />
          ) : (
            <Button
              onClick={() => models.handleMark()}
              appearance="primary"
              loading={models.loading}
              disabled={models.isMarkButtonDisabled}
              customDataAttributes={{
                container: { 'data-track': 'mark-items-for-fulfillment' },
              }}
            >
              {models.markButtonLabel}
            </Button>
          )}
        </div>
      </StepBreadcrumbs>
    </div>
  )
}
